import { LogMetadata } from "../models/log-metadata";

export const isApp = () => typeof window.invokeCSharpAction === "function";

/**
 * @summary Sends request to app and returns promise that resolves when app sends response back.
 * @param key Key value that app is responding to (this needs to exist in the app)
 * @param value Value to be sent with the request
 * @param options
 * @returns Promise with string value responce
 */
export const getValueFromApp = <T = string>(
	key: string,
	value?: string,
	options?: { timeout?: number },
): Promise<T> => {
	window.invokeCSharpAction(key, value);

	return new Promise((resolve, reject) => {
		const resolveEvent = (obj: unknown) => {
			const value = obj as EventPayload<T>;

			clearTimeout(timeout);
			resolve(value.detail);
		};

		window.addEventListener(key, resolveEvent, { once: true });

		const timeout = setTimeout(() => {
			window.removeEventListener(key, resolveEvent);
			reject(new Error("no responce was initiated"));
		}, options?.timeout ?? 5000);
	});
};

type EventPayload<T = string> = {
	detail: T;
};

export enum ColorMode {
	Unspecified = "Unspecified",
	Light = "Light",
	Dark = "Dark",
}

type appFunctions = {
	openBrowserWindow: string;
	changeApplicationUrl: string;
	downloadFile: string;
	openFile: string;
	launchApp: string;
	closeApp: string;
	checkAppToastFinish: string;
	setAppTheme: string;
	setLoggingMetadata: string;
};

const runAppFunction = (functionName: keyof appFunctions, value?: string) => {
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/unbound-method
	const func = window[functionName as keyof typeof window];

	if (isApp() && func && typeof func === "function") {
		// eslint-disable-next-line @typescript-eslint/no-unsafe-call
		func(value ?? "");
	} else {
		throw new Error("Unsupported method");
	}
};

export const haveAppFunction = (functionName: keyof appFunctions) => {
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/unbound-method
	const func = window[functionName as keyof typeof window];

	return isApp() && Boolean(func) && typeof func === "function";
};

export const openBrowserWindow = (url: string) => {
	runAppFunction("openBrowserWindow", url);
};

export const changeApplicationUrl = () => {
	runAppFunction("changeApplicationUrl");
};

export const downloadFile = (data: DownloadFileDTO) => {
	runAppFunction("downloadFile", JSON.stringify(data));
};

export const openFile = (data: DownloadFileDTO) => {
	runAppFunction("openFile", JSON.stringify(data));
};

export const launchApp = (urls: string[]) => {
	runAppFunction("launchApp", JSON.stringify(urls));
};

export const closeApp = () => {
	runAppFunction("closeApp");
};

export const setBiometricsToastShown = () => {
	runAppFunction("checkAppToastFinish", "Biometrics");
};

export const setAppTheme = (theme: ColorMode) => {
	runAppFunction("setAppTheme", theme);
};

export const setLoggingMetadata = (logMetadata: LogMetadata) => {
	runAppFunction("setLoggingMetadata", JSON.stringify(logMetadata));
};
